import { useTranslation } from 'next-i18next';
import { FC, useCallback, useMemo } from 'react';
import { useForm, useFormState } from 'react-final-form';
import SkeletonBlock from 'src/common/components/SkeletonLoader/SkeletonBlock';
import { useListingMotivators } from 'src/listing/hooks/useListingPageMotivators';
import { TripCartFormState } from 'src/listing/types';
import TripCartDropdown, { TripCartDropdownOption } from 'src/trip-cart/components/TripCartDropdown';
import { useTripCartOptionsContext } from 'src/trip-cart/hooks/useTripCartOptionsContext';

type Values = 'true' | 'false';

const TripCartMultiDayDropdown: FC = () => {
  const [t] = useTranslation('listing-page');
  const { change } = useForm<TripCartFormState>();
  const { values } = useFormState<TripCartFormState>();
  const { options } = useTripCartOptionsContext();
  const { setIsListingMultiDayState } = useListingMotivators();
  const items = useMemo(() => {
    const isSingle = options.isMultiday.length === 1;
    const list: TripCartDropdownOption<Values>[] = [
      {
        id: 'false',
        label: t(isSingle ? 'trip-cart--same-day-only' : 'trip-cart--same-day-toggle'),
        hasInstabook: false,
        icon: 'sun',
      },
      {
        id: 'true',
        label: t(isSingle ? 'trip-cart--multi-day-only' : 'trip-cart--multi-day-toggle'),
        hasInstabook: false,
        icon: 'duration-overnight',
      },
    ];

    if (options.isMultiday.length > 1) {
      return list;
    }

    // remove unused
    if (options.isMultiday[0]) {
      list.shift();
    } else {
      list.pop();
    }
    return list;
  }, [options.isMultiday, t]);

  const selected = useMemo((): TripCartDropdownOption<Values> => (
    items.find((item) => item.id === values.isMultiDay?.toString())
  ) ?? items[0], [items, values.isMultiDay]);

  const onSelect = useCallback((option: TripCartDropdownOption<Values>) => {
    change('isMultiDay', option.id === 'true');
    setIsListingMultiDayState(option.id === 'true');
  }, [change, setIsListingMultiDayState]);

  return (
    <SkeletonBlock loading={!items}>
      <TripCartDropdown
        id="isMultiDay"
        options={items ?? []}
        onSelect={onSelect}
        selectedOption={selected}
      />
    </SkeletonBlock>
  );
};

export default TripCartMultiDayDropdown;
